import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { dataAPI, vaultsAPI } from '../services/api';
import useVaultItems from '../hooks/useVaultItems';
import { CATEGORIES, CATEGORY_ICON } from '../utils/itemTypes';
import { Card, Chip, HealthBar, ScoreBar, EmptyState, IconCircle, PillButton, Modal, Input, FeatureBanner } from '../ui';
import { ReactComponent as AlertTriangleIcon } from '../assets/icons/alert-triangle.svg';
import { ReactComponent as EyeIcon } from '../assets/icons/eye.svg';
import { ReactComponent as EyeOffIcon } from '../assets/icons/eye-off.svg';
import { ReactComponent as CopyIcon } from '../assets/icons/copy.svg';
import { ReactComponent as PasswordDotsIcon } from '../assets/icons/password-dots.svg';
import { ReactComponent as MoreIcon } from '../assets/icons/more-horizontal.svg';
import { ReactComponent as ActivityIcon } from '../assets/icons/activity.svg';
import { ReactComponent as FolderIcon } from '../assets/icons/tag.svg';
import { ReactComponent as TagIcon } from '../assets/icons/tag.svg';
import { ReactComponent as PlusIcon } from '../assets/icons/plus.svg';
import './Dashboard.css';

// Mismo servicio que usa Vault.js para el favicon (Vault.js l.1740): Google
// s2/favicons, sin clave y sin dependencia nueva.
// Un host local o una IP privada no tiene favicon público: pedirlo a Google
// es un 404 seguro y ruidoso en la consola.
const IPV4_RE = /^(\d{1,3}\.){3}\d{1,3}$/;
function isLocalHost(host) {
  if (!host) return true;
  const h = host.toLowerCase();
  if (h === 'localhost' || h.endsWith('.localhost') || h.endsWith('.local') || h.includes(':')) return true;
  if (IPV4_RE.test(h)) {
    const [a] = h.split('.').map(Number);
    return a === 127 || a === 10 || a === 0 || h.startsWith('192.168.') || h.startsWith('169.254.') || /^172\.(1[6-9]|2\d|3[01])\./.test(h);
  }
  return false;
}
function faviconFor(url) {
  if (!url) return null;
  try {
    const domain = url.includes('://') ? new URL(url).hostname : url;
    if (!domain || !domain.includes('.') || isLocalHost(domain)) return null;
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
  } catch {
    return null;
  }
}

function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const rfidId = localStorage.getItem('rfidId');

  const [category, setCategory] = useState('all');
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedVault, setSelectedVault] = useState('personal');

  const [stats, setStats] = useState(null);
  // Con una sola bóveda (Personal), filtrar por ella equivale a "todo" y evita
  // la ruta lenta del servidor que descifra el vault entero para filtrar.
  const otherVaultsExist = Object.keys(stats?.byVault || {}).some((k) => k !== 'personal');
  const effectiveVault = selectedVault === 'personal' && !otherVaultsExist ? 'all' : selectedVault;
  const { items, loading, totalItems, hasMore, loadMore, cardName } = useVaultItems({
    limit: 20, type: category, tag: selectedTag, vault: effectiveVault,
  });

  const [health, setHealth] = useState(null);
  const [mostUsed, setMostUsed] = useState([]);
  const [vaults, setVaults] = useState([]);
  const [showNewVault, setShowNewVault] = useState(false);
  const [newVaultName, setNewVaultName] = useState('');
  const [creatingVault, setCreatingVault] = useState(false);
  const [revealedIds, setRevealedIds] = useState({});
  const [passwords, setPasswords] = useState({});

  const reloadStats = useCallback(() => {
    if (!rfidId) return;
    dataAPI.stats(rfidId).then((res) => setStats(res.data)).catch(() => setStats(null));
  }, [rfidId]);

  const reloadVaults = useCallback(() => {
    if (!rfidId) return;
    vaultsAPI.list(rfidId).then((res) => setVaults(res.data?.vaults || [])).catch(() => setVaults([]));
  }, [rfidId]);

  useEffect(() => {
    if (!rfidId) return;
    api.get(`/security/health/${rfidId}`).then((res) => setHealth(res.data)).catch(() => setHealth(null));
    api.get(`/security/usage/${rfidId}`, { params: { days: 30, limit: 5 } })
      .then((res) => setMostUsed(res.data || []))
      .catch(() => setMostUsed([]));
    reloadStats();
    reloadVaults();
  }, [rfidId, reloadStats, reloadVaults]);

  const healthByItemId = useMemo(() => {
    const map = new Map();
    (health?.items || []).forEach((h) => map.set(String(h.id), h));
    return map;
  }, [health]);

  // Contadores de toda la cuenta (no solo la página cargada): /data/:rfidId/stats.
  const categoryChips = useMemo(() => CATEGORIES.map((c) => {
    let count;
    if (c.id === 'all') count = stats?.total ?? totalItems;
    else if (c.id === 'favorites') count = stats?.favorites ?? 0;
    else count = stats?.byType?.[c.id] ?? 0;
    return { ...c, count };
  }), [stats, totalItems]);

  const tagChips = useMemo(() => (
    Object.entries(stats?.byTag || {})
      .sort((a, b) => b[1] - a[1])
      .map(([tag, count]) => ({ tag, count }))
  ), [stats]);

  const mostUsedTiles = useMemo(() => (
    mostUsed.map((u) => {
      const full = items.find((i) => String(i.id) === String(u.itemId));
      return {
        id: u.itemId,
        name: u.name,
        username: full?.username || '',
        url: full?.url || '',
        count: u.count,
      };
    })
  ), [mostUsed, items]);

  const toggleReveal = useCallback(async (id) => {
    if (revealedIds[id]) {
      setRevealedIds((prev) => ({ ...prev, [id]: false }));
      return;
    }
    if (!passwords[id]) {
      try {
        const res = await dataAPI.get(id, rfidId);
        const data = typeof res.data?.data === 'string' ? JSON.parse(res.data.data) : (res.data?.data || {});
        setPasswords((prev) => ({ ...prev, [id]: data.password || '' }));
      } catch {
        setPasswords((prev) => ({ ...prev, [id]: '' }));
      }
    }
    setRevealedIds((prev) => ({ ...prev, [id]: true }));
  }, [passwords, revealedIds, rfidId]);

  const copyPassword = useCallback(async (id) => {
    let pwd = passwords[id];
    if (!pwd) {
      try {
        const res = await dataAPI.get(id, rfidId);
        const data = typeof res.data?.data === 'string' ? JSON.parse(res.data.data) : (res.data?.data || {});
        pwd = data.password || '';
        setPasswords((prev) => ({ ...prev, [id]: pwd }));
      } catch {
        return;
      }
    }
    if (pwd) {
      await navigator.clipboard.writeText(pwd);
      api.post('/security/usage', { itemId: id, source: 'web', action: 'copy' }).catch(() => {});
    }
  }, [passwords, rfidId]);

  const createVault = useCallback(async () => {
    const name = newVaultName.trim();
    if (!name || !rfidId) return;
    setCreatingVault(true);
    try {
      const vaultId = name.toLowerCase().replace(/\s+/g, '-');
      await vaultsAPI.create({ rfidId, name, vaultId });
      setNewVaultName('');
      setShowNewVault(false);
      reloadVaults();
    } catch {
      // el modal se queda abierto; el usuario ve que no pasó nada y reintenta
    } finally {
      setCreatingVault(false);
    }
  }, [newVaultName, rfidId, reloadVaults]);

  return (
    <div className="sgc-dashboard">
      <div className="sgc-dashboard__header">
        <div>
          <h1 className="sgc-dashboard__greeting">{t('dashboard.greeting', { card: cardName || '—' })}</h1>
          <p className="sgc-dashboard__subtitle">{t('dashboard.itemCount', { count: stats?.total ?? totalItems })}</p>
        </div>
        {health && (
          <button type="button" className="sgc-dashboard__health-pill" onClick={() => navigate('/health')}>
            <span>{t('dashboard.healthPill')}</span>
            <HealthBar score={health.summary.healthScore} />
          </button>
        )}
      </div>

      {stats?.undecryptable > 0 && (
        <FeatureBanner
          icon={AlertTriangleIcon}
          tone="warn"
          title={t('dashboard.undecryptableTitle', { count: stats.undecryptable })}
          subtitle={t('dashboard.undecryptableDesc')}
        />
      )}

      {mostUsedTiles.length > 0 && (
        <section className="sgc-dashboard__section">
          <h2 className="sgc-dashboard__section-title">{t('dashboard.mostUsed')}</h2>
          <div className="sgc-dashboard__tiles">
            {mostUsedTiles.map((tile) => (
              <Card key={tile.id} className="sgc-tile">
                <div className="sgc-tile__top">
                  {faviconFor(tile.url) && (
                    <img
                      src={faviconFor(tile.url)}
                      alt=""
                      className="sgc-tile__favicon"
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                    />
                  )}
                  <span className="sgc-tile__favicon-fallback" style={{ display: faviconFor(tile.url) ? 'none' : 'flex' }}>
                    <IconCircle icon={FolderIcon} size={32} />
                  </span>
                  <button type="button" className="sgc-tile__more" onClick={() => copyPassword(tile.id)} title={t('dashboard.copy')}>
                    <MoreIcon width={16} height={16} aria-hidden="true" />
                  </button>
                </div>
                <p className="sgc-tile__name">{tile.name}</p>
                <p className="sgc-tile__username">{tile.username || '—'}</p>
                <div className="sgc-tile__actions">
                  <PasswordDotsIcon width={18} height={18} aria-hidden="true" className="sgc-tile__dots" />
                  <button type="button" onClick={() => copyPassword(tile.id)} title={t('dashboard.copy')}>
                    <CopyIcon width={16} height={16} aria-hidden="true" />
                  </button>
                </div>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Bóvedas: selector + "crear vault" */}
      <div className="sgc-dashboard__vaults">
        {/* "Personal" es la bóveda implícita de todo ítem sin `vault` (o
            vault === 'personal'); siempre existe aunque /api/vaults no la liste. */}
        <Chip
          label={`${t('dashboard.personalVault')} (${stats?.byVault?.personal ?? 0})`}
          selected={selectedVault === 'personal'}
          onClick={() => setSelectedVault('personal')}
        />
        {vaults.filter((v) => v.vaultId !== 'personal').map((v) => (
          <Chip
            key={v.vaultId}
            label={`${v.name} (${stats?.byVault?.[v.vaultId] ?? 0})`}
            selected={selectedVault === v.vaultId}
            onClick={() => setSelectedVault(v.vaultId)}
          />
        ))}
        <Chip label={t('dashboard.allVaults')} selected={selectedVault === 'all'} onClick={() => setSelectedVault('all')} />
        <button type="button" className="sgc-dashboard__add-vault" onClick={() => setShowNewVault(true)} title={t('dataEntry.createVault')}>
          <PlusIcon width={14} height={14} aria-hidden="true" />
        </button>
      </div>

      {/* Categorías, con contadores de toda la cuenta */}
      <div className="sgc-dashboard__chips">
        {categoryChips.map((c) => (
          <Chip
            key={c.id}
            icon={CATEGORY_ICON[c.id] || FolderIcon}
            label={t(c.labelKey)}
            count={c.count}
            selected={category === c.id}
            onClick={() => setCategory(c.id)}
          />
        ))}
      </div>

      {/* Tags */}
      {tagChips.length > 0 && (
        <div className="sgc-dashboard__chips sgc-dashboard__chips--tags">
          <Chip label={t('dashboard.tags')} selected={!selectedTag} onClick={() => setSelectedTag(null)} icon={TagIcon} />
          {tagChips.map(({ tag, count }) => (
            <Chip key={tag} label={tag} count={count} selected={selectedTag === tag} onClick={() => setSelectedTag(tag)} />
          ))}
        </div>
      )}

      <Card padded={false} className="sgc-dashboard__table-card">
        {loading && items.length === 0 ? (
          <p className="sgc-dashboard__loading">{t('common.loading', 'Cargando…')}</p>
        ) : items.length === 0 ? (
          <EmptyState icon={ActivityIcon} title={t('dashboard.emptyTitle')} description={t('dashboard.emptyDescription')} />
        ) : (
          <>
            <table className="sgc-table">
              <thead>
                <tr>
                  <th>{t('dashboard.col.site')}</th>
                  <th>{t('dashboard.col.category')}</th>
                  <th>{t('dashboard.col.user')}</th>
                  <th>{t('dashboard.col.password')}</th>
                  <th>{t('dashboard.col.vulnerability')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const h = healthByItemId.get(String(item.id));
                  return (
                    <tr key={item.id} className="sgc-table__row" onClick={() => navigate(`/vault/${item.id}`)}>
                      <td>
                        <div className="sgc-table__site">
                          {faviconFor(item.url) && (
                            <img
                              src={faviconFor(item.url)}
                              alt=""
                              className="sgc-table__favicon"
                              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                            />
                          )}
                          <span className="sgc-table__favicon-fallback" style={{ display: faviconFor(item.url) ? 'none' : 'flex' }}>
                            <IconCircle icon={CATEGORY_ICON[item.category] || FolderIcon} size={28} />
                          </span>
                          <span>{item.title}</span>
                        </div>
                      </td>
                      <td>
                        <Chip label={t(`dashboard.categories.${item.category}`, item.category)} />
                      </td>
                      <td className="sgc-table__muted">{item.username || '—'}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="sgc-table__password">
                          <span className="sgc-table__password-value">
                            {revealedIds[item.id] ? (passwords[item.id] || '—') : '••••••••'}
                          </span>
                          <button type="button" onClick={() => toggleReveal(item.id)} title={t('dashboard.reveal')}>
                            {revealedIds[item.id] ? (
                              <EyeOffIcon width={16} height={16} aria-hidden="true" />
                            ) : (
                              <EyeIcon width={16} height={16} aria-hidden="true" />
                            )}
                          </button>
                          <button type="button" onClick={() => copyPassword(item.id)} title={t('dashboard.copy')}>
                            <CopyIcon width={16} height={16} aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                      <td>
                        {h ? (
                          <ScoreBar value={h.score} label={`${100 - h.score}%`} />
                        ) : (
                          <span className="sgc-table__muted">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {hasMore && (
              <div className="sgc-dashboard__load-more">
                <PillButton variant="secondary" onClick={loadMore} loading={loading}>
                  {t('dashboard.loadMore')}
                </PillButton>
              </div>
            )}
          </>
        )}
      </Card>

      <Modal open={showNewVault} onClose={() => setShowNewVault(false)} title={t('dataEntry.createVault')}>
        <Input
          label={t('dataEntry.name')}
          value={newVaultName}
          onChange={(e) => setNewVaultName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') createVault(); }}
        />
        <PillButton disabled={!newVaultName.trim() || creatingVault} loading={creatingVault} onClick={createVault}>
          {t('common.save', 'Guardar')}
        </PillButton>
      </Modal>
    </div>
  );
}

export default Dashboard;
